import { Component, Event, EventEmitter, Host, Prop, h } from '@stencil/core';

import { onStateFieldAnswered } from '../../../actions/actions.answers';
import { onFieldValidated } from '../../../actions/actions.validation';
import { FieldValidationResult } from '../../../interfaces/interface.validation';
import {
  formatPhone,
  formatZip,
  isValidEmail,
  isValidName,
  isValidPhone,
  isValidZip,
  normalizeName,
  normalizePhone,
  normalizeZip,
  required,
} from '../../../utils/utils.validation';

@Component({
  tag: 'question-text',
  styleUrl: 'question-text.css',
})
export class QuestionText {
  @Prop() errorMessage: string = undefined;
  @Prop() heading: string = '';
  @Prop() label: string = '';
  @Prop() canonicalName: string = '';
  @Prop() fieldName: string = '';
  @Prop() required: boolean = false;
  @Prop() subHeading: string = '';
  @Prop() type: 'name' | 'phone' | 'email' | 'zip' | 'text' = 'text';
  @Prop({ mutable: true }) value: string = '';
  @Prop({ mutable: true }) isDirty: boolean = false;
  @Event({ bubbles: false }) validation: EventEmitter<FieldValidationResult>;
  @Prop({ mutable: true }) hasError: boolean = false;

  render() {
    let inputType, parse, format;
    switch (this.type) {
      case 'name':
        parse = normalizeName;
        break;
      case 'phone':
        parse = normalizePhone;
        format = formatPhone;
      case 'email':
      case 'text':
        inputType = this.type;
        break;
      case 'zip':
        parse = normalizeZip;
        format = formatZip;
        inputType = 'number';
        break;
      default:
        inputType = 'text';
        break;
    }

    return (
      <Host>
        {this.heading && <div class="text-md font-bold">{this.heading}</div>}
        {this.subHeading && <div class="pb-4">{this.subHeading}</div>}
        <input-text-box
          type={inputType}
          label={this.label}
          value={this.value}
          hasError={this.isDirty && this.hasError}
          errorMessage={this.getErrorMessage()}
          onValueChange={(e) => this.onInputChange(e)}
          onBlur={(e) => this.onInputBlur(e)}
          parse={parse}
          format={format}
        />
      </Host>
    );
  }

  componentWillLoad() {
    // Pulse the current validation state so that consumers have an initial value
    onFieldValidated(this.canonicalName, this.fieldName, this.isValid());
    this.validate();
  }

  onInputChange(evt) {
    this.value = evt.detail;
    if (this.hasError) {
      this.hasError = false;
    }
    onFieldValidated(this.canonicalName, this.fieldName, this.isValid());
    onStateFieldAnswered(this.canonicalName, this.fieldName, evt.detail);
  }

  onInputBlur(_) {
    this.isDirty = true;
    this.validate();
  }

  validate() {
    const isValid = this.isValid();

    this.validation.emit({
      isValid,
      isDirty: this.isDirty,
    });

    this.hasError = !isValid;
  }

  isValid() {
    if (this.hasError) {
      return false;
    }
    const value = this.value || '';
    let isValid = true;

    for (const validate of this.getValidators()) {
      isValid &&= validate(value);
    }

    return isValid;
  }

  getErrorMessage() {
    if (this.errorMessage !== undefined) {
      return this.errorMessage;
    }

    switch (this.type) {
      case 'phone':
        return 'Please enter a valid phone number.';
      case 'email':
        return 'Please enter a valid email.';
      case 'zip':
        return 'Please enter a valid phone ZIP code.';
      default:
        return 'Please enter a value.';
    }
  }

  *getValidators() {
    if (this.required) {
      yield required;
    }

    switch (this.type) {
      case 'name':
        yield isValidName;
        break;
      case 'phone':
        yield isValidPhone;
        break;
      case 'email':
        yield isValidEmail;
        break;
      case 'zip':
        yield isValidZip;
        break;
    }
  }
}
